import React from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { graphQlBrowseApiRef } from '@backstage-community/plugin-graphiql';
import { GraphiQL } from 'graphiql';
import 'graphiql/graphiql.css';
import { Progress } from '@backstage/core-components';
import { Box } from '@material-ui/core';
import {
    EntityApiDefinitionCard,
    EntityConsumingComponentsCard,
    EntityProvidingComponentsCard,
} from '@backstage/plugin-api-docs';
import {
    EntitySwitch,
    EntityOrphanWarning,
    EntityProcessingErrorsPanel,
    EntityLinksCard,
    isOrphan,
    hasCatalogProcessingErrors,
    hasRelationWarnings,
    EntityRelationWarning,
} from '@backstage/plugin-catalog';
import { EntityCatalogGraphCard } from '@backstage/plugin-catalog-graph';
import { TabbedLayout, Content } from '@backstage/core-components';
import { Grid } from '@material-ui/core';
import { SystemAboutCard } from './SystemAboutCard';
import { CustomerEntityBanner } from './CustomEntityBanner';

const entityWarningContent = (
    <>
        <EntitySwitch>
            <EntitySwitch.Case if={isOrphan}>
                <Grid item xs={12}>
                    <EntityOrphanWarning />
                </Grid>
            </EntitySwitch.Case>
        </EntitySwitch>
        <EntitySwitch>
            <EntitySwitch.Case if={hasRelationWarnings}>
                <Grid item xs={12}>
                    <EntityRelationWarning />
                </Grid>
            </EntitySwitch.Case>
        </EntitySwitch>
        <EntitySwitch>
            <EntitySwitch.Case if={hasCatalogProcessingErrors}>
                <Grid item xs={12}>
                    <EntityProcessingErrorsPanel />
                </Grid>
            </EntitySwitch.Case>
        </EntitySwitch>
    </>
);

function EntityGraphiQLTab() {
    const { entity } = useEntity();
    const graphQlApi = useApi(graphQlBrowseApiRef);
    const [fetcher, setFetcher] = React.useState<((body: any) => Promise<any>) | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [notFound, setNotFound] = React.useState(false);

    React.useEffect(() => {
        let mounted = true;
        graphQlApi.getEndpoints().then(endpoints => {
            if (!mounted) return;
            const id = `${entity.metadata.namespace ?? 'default'}--${entity.metadata.name}`;
            const ep = endpoints.find(e => e.id === id);
            if (ep) {
                setFetcher(() => ep.fetcher);
            } else {
                setNotFound(true);
            }
            setLoading(false);
        });
        return () => { mounted = false; };
    }, [graphQlApi, entity]);

    if (loading) return <Progress />;

    if (notFound) {
        return (
            <Box p={2}>
                <p>
                    No GraphQL endpoint configured for this API. Add the annotation{' '}
                    <code>causeway.com/graphql-endpoint: /your/graphql/path</code> to the catalog entity.
                </p>
            </Box>
        );
    }

    return (
        <Box style={{ height: '70vh', display: 'flex', flexDirection: 'column' }}>
            <GraphiQL fetcher={fetcher!} />
        </Box>
    );
}

export const GraphQLAPIPage = () => (
    <Box style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <CustomerEntityBanner />
        <Box style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            <Content>
                <TabbedLayout>
                    <TabbedLayout.Route path="/" title="Definition">
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <EntityApiDefinitionCard />
                            </Grid>
                        </Grid>
                    </TabbedLayout.Route>

                    <TabbedLayout.Route path="/graphiql" title="Try It">
                        <EntityGraphiQLTab />
                    </TabbedLayout.Route>

                    <TabbedLayout.Route path="/overview" title="Overview">
                        <Grid container spacing={3}>
                            {entityWarningContent}
                            <Grid item md={12}>
                                <SystemAboutCard />
                            </Grid>
                            <Grid item md={12} xs={12}>
                                <EntityCatalogGraphCard variant="gridItem" height={400} />
                            </Grid>
                            <Grid item md={12} xs={12}>
                                <EntityLinksCard />
                            </Grid>
                            <Grid container item md={12}>
                                <Grid item md={6}>
                                    <EntityProvidingComponentsCard />
                                </Grid>
                                <Grid item md={6}>
                                    <EntityConsumingComponentsCard />
                                </Grid>
                            </Grid>
                        </Grid>
                    </TabbedLayout.Route>
                </TabbedLayout>
            </Content>
        </Box>
    </Box>
);
